"use client"

import Link from "next/link"
import Image from "next/image"
import { usePathname } from "next/navigation"
import { Menu } from "lucide-react"
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet"
import { SignInButton, UserButton } from "@clerk/nextjs"
import { Authenticated, Unauthenticated, AuthLoading } from "convex/react"

export function SharedHeader() {
  const pathname = usePathname()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const navItems = [
    { href: "/showcase", label: "Our Work" },
    { href: "/about", label: "About" },
    { href: "/faq", label: "FAQ" },
  ]

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto flex h-16 items-center justify-between px-4">
        <Link href="/" className="flex items-center space-x-2">
          <Image 
            src="/images/trpc-logo.png" 
            alt="TRPC" 
            width={24} 
            height={24} 
            className="h-8 w-auto"
            quality={100}
            priority
            unoptimized
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-1">
          {navItems.map((item) => (
            <Link key={item.href} href={item.href}>
              <Button 
                variant="ghost"
                className={pathname === item.href 
                  ? "h-9 px-3 bg-white/10 backdrop-blur border border-zinc-600 text-zinc-100 hover:bg-white/15" 
                  : "h-9 px-3 bg-transparent hover:bg-white/5 text-zinc-300 hover:text-zinc-100"}
              >
                {item.label}
              </Button>
            </Link>
          ))}
          
          {/* Admin link for authenticated users */}
          <Authenticated>
            <Link href="/admin">
              <Button 
                variant="ghost"
                className={pathname.startsWith("/admin") 
                  ? "h-9 px-3 bg-white/10 backdrop-blur border border-zinc-600 text-zinc-100 hover:bg-white/15" 
                  : "h-9 px-3 bg-transparent hover:bg-white/5 text-zinc-300 hover:text-zinc-100"}
              >
                Admin
              </Button>
            </Link>
          </Authenticated>
          
          {/* Auth buttons */}
          <div className="ml-auto pl-8 flex items-center space-x-3">
            <AuthLoading>
              <div className="h-9 w-20 animate-pulse bg-zinc-800 rounded" />
            </AuthLoading>
            <Authenticated>
              <UserButton 
                afterSignOutUrl="/"
                appearance={{
                  elements: {
                    avatarBox: "h-9 w-9"
                  }
                }}
              />
            </Authenticated>
            <Unauthenticated>
              <SignInButton mode="modal">
                <Button 
                  className="h-9 px-3 bg-white/10 backdrop-blur border border-zinc-600 text-zinc-100 hover:bg-white/15"
                >
                  Sign In
                </Button>
              </SignInButton>
            </Unauthenticated>
          </div>
        </nav>

        {/* Mobile Navigation */}
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="md:hidden">
              <Menu className="h-5 w-5" />
              <span className="sr-only">Toggle menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[300px] sm:w-[400px]">
            <nav className="flex flex-col space-y-3 mt-8">
              {navItems.map((item) => (
                <Link key={item.href} href={item.href}>
                  <Button 
                    variant="ghost"
                    className={pathname === item.href 
                      ? "w-full justify-start h-11 bg-white/10 backdrop-blur border border-zinc-600 text-zinc-100 hover:bg-white/15"
                      : "w-full justify-start h-11 bg-transparent hover:bg-white/5 text-zinc-300 hover:text-zinc-100"}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Button>
                </Link>
              ))}
              
              {/* Admin link for authenticated users */}
              <Authenticated>
                <Link href="/admin">
                  <Button 
                    variant="ghost"
                    className={pathname.startsWith("/admin") 
                      ? "w-full justify-start h-11 bg-white/10 backdrop-blur border border-zinc-600 text-zinc-100 hover:bg-white/15"
                      : "w-full justify-start h-11 bg-transparent hover:bg-white/5 text-zinc-300 hover:text-zinc-100"}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Admin
                  </Button>
                </Link>
              </Authenticated>
              
              {/* Auth section */}
              <div className="pt-4 border-t border-zinc-700 space-y-3">
                <AuthLoading>
                  <div className="h-11 w-full animate-pulse bg-zinc-800 rounded" />
                </AuthLoading>
                <Authenticated>
                  <div className="flex items-center justify-between px-3">
                    <span className="text-sm text-zinc-400">Account</span>
                    <UserButton 
                      afterSignOutUrl="/"
                      appearance={{
                        elements: {
                          avatarBox: "h-8 w-8"
                        }
                      }}
                    />
                  </div>
                </Authenticated>
                <Unauthenticated>
                  <SignInButton mode="modal">
                    <Button className="w-full justify-start h-11 bg-white/10 backdrop-blur border border-zinc-600 text-zinc-100 hover:bg-white/15">
                      Sign In
                    </Button>
                  </SignInButton>
                </Unauthenticated>
              </div>
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  )
}
